import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import {
  CUSTOM_OPTION,
  CustomNameDialog,
  REFERRAL_SOURCES,
  formatDoctorName,
  isCustomReferral,
  referralName,
  toCustomId,
} from "./ReferredByDoctorField";

/**
 * Picks the doctor who ordered one invoice line. `value` is the doctor_id sent to the
 * backend as `ordered_by_doctor_id`; `doctors` comes from useFacilityDoctors().
 * Shows the doctor next to an icon; the icon opens a searchable modal to change it.
 */
export default function OrderedByDoctorSelect({
  doctors,
  loading,
  value,
  onChange,
  disabled = false,
  locked = false,
  error = false,
  customNames = [],
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customOpen, setCustomOpen] = useState(false);

  // Self / External / "Custom name..." come first, then custom names already used on this
  // invoice (`customNames` are custom ids), then the facility doctors.
  const options = useMemo(() => {
    const customs = [...new Set([...customNames, value].filter(isCustomReferral))];
    return [
      ...REFERRAL_SOURCES,
      CUSTOM_OPTION,
      ...customs.map((id) => ({ doctor_id: id, name: referralName(id, []) })),
      ...(doctors || []).filter((doctor) => doctor.doctor_id),
    ];
  }, [doctors, customNames, value]);
  const selected =
    options.find((doctor) => String(doctor.doctor_id) === String(value)) ||
    null;

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((doctor) =>
      formatDoctorName(doctor).toLowerCase().includes(term),
    );
  }, [options, query]);

  // Bring the current doctor into view when the modal opens on a long list.
  const scrollToSelected = (node) => {
    if (node) node.scrollIntoView({ block: "center" });
  };

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const pick = (doctor) => {
    if (locked) return;
    if (doctor.doctor_id === CUSTOM_OPTION.doctor_id) {
      setCustomOpen(true);
      return;
    }
    onChange(doctor.doctor_id);
    close();
  };

  return (
    <>
      <Box
        sx={{ display: "flex", alignItems: "center", minHeight: 38 }}
      >
        <Tooltip title={
            selected
              ? locked
                ? `${formatDoctorName(selected)} (locked)`
                : `${formatDoctorName(selected)} (click to change)`
              : "Select ordering doctor"
          }>
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => setOpen(true)}
              aria-label="Select ordered by doctor"
              sx={{ color: error ? "#d32f2f" : selected ? "#125386" : "#808285" }}
            >
              <ManageAccountsIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Ordered by doctor
          <IconButton size="small" onClick={close} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                backgroundColor: "#e3f0fb",
                borderLeft: "3px solid #125386",
              }}
            >
              <CheckIcon fontSize="small" sx={{ color: "#125386" }} />
              <Typography
                variant="body2"
                sx={{ color: "#125386", fontWeight: 600 }}
              >
                {formatDoctorName(selected)}
              </Typography>
            </Box>
          ) : null}
          {locked ? (
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#808285", mb: 1 }}
            >
              This doctor is fixed for a consultation fee and cannot be changed.
            </Typography>
          ) : null}
          {!locked && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search doctor"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <CircularProgress size={16} />
              ) : null,
            }}
            sx={{ mb: 1 }}
          />
          )}
          {!locked && (
          <List dense sx={{ maxHeight: 320, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: "#808285", textAlign: "center", py: 2 }}
              >
                {loading ? "Loading doctors..." : "No doctors found"}
              </Typography>
            ) : (
              filtered.map((doctor) => {
                const isSelected = String(doctor.doctor_id) === String(value);
                return (
                  <ListItemButton
                    key={doctor.doctor_id}
                    selected={isSelected}
                    ref={isSelected ? scrollToSelected : undefined}
                    onClick={() => pick(doctor)}
                    disabled={locked && !isSelected}
                    sx={
                      isSelected
                        ? {
                            backgroundColor: "#e3f0fb !important",
                            borderLeft: "3px solid #125386",
                          }
                        : undefined
                    }
                  >
                    <ListItemText
                      primary={formatDoctorName(doctor)}
                      primaryTypographyProps={{
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? "#125386" : undefined,
                      }}
                    />
                    {isSelected ? (
                      <CheckIcon fontSize="small" sx={{ color: "#125386" }} />
                    ) : null}
                  </ListItemButton>
                );
              })
            )}
          </List>
          )}
        </DialogContent>
      </Dialog>
      <CustomNameDialog
        open={customOpen}
        title="Custom ordering name"
        onClose={() => setCustomOpen(false)}
        onSubmit={(name) => {
          onChange(toCustomId(name));
          close();
        }}
      />
    </>
  );
}
